import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useNCRList,
  useNCRStats,
  getStatusLabel,
  getStatusColor,
  getSeverityLabel,
  getSeverityColor,
  getTypeLabel,
  NCRStatus,
  NCRSeverity,
  NCRType,
} from '@/hooks/useNCR';
import { NCRFormDialog } from './NCRFormDialog';
import { NCRDetailsDialog } from './NCRDetailsDialog';
import {
  Plus,
  Search,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  Eye,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

interface NCRListProps {
  canManage?: boolean;
}

export function NCRList({ canManage = false }: NCRListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<NCRStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<NCRSeverity | 'all'>('all');

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedNCRId, setSelectedNCRId] = useState<string | null>(null);
  const [editingNCR, setEditingNCR] = useState<any>(null);

  const { data: ncrs, isLoading } = useNCRList({
    status: statusFilter,
    severity: severityFilter,
  });
  const { data: stats } = useNCRStats();

  const filteredNCRs = ncrs?.filter((ncr) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ncr.ncr_number.toLowerCase().includes(search) ||
      ncr.title.toLowerCase().includes(search) ||
      ncr.work_order_number?.toLowerCase().includes(search) ||
      ncr.reporter_name?.toLowerCase().includes(search)
    );
  });

  const handleViewNCR = (id: string) => {
    setSelectedNCRId(id);
    setDetailsDialogOpen(true);
  };

  const handleEditNCR = () => {
    const ncr = ncrs?.find((n) => n.id === selectedNCRId);
    if (ncr) {
      setEditingNCR(ncr);
      setDetailsDialogOpen(false);
      setFormDialogOpen(true);
    }
  };

  const handleCloseForm = () => {
    setFormDialogOpen(false);
    setEditingNCR(null);
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <AlertTriangle className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_ncrs || 0}</p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Clock className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.open_ncrs || 0}</p>
                <p className="text-sm text-muted-foreground">Abiertas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/10">
                <Search className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.investigation_ncrs || 0}</p>
                <p className="text-sm text-muted-foreground">Investigando</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.closed_ncrs || 0}</p>
                <p className="text-sm text-muted-foreground">Cerradas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <XCircle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.overdue_ncrs || 0}</p>
                <p className="text-sm text-muted-foreground">Vencidas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Clock className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.avg_days_to_close || 0}</p>
                <p className="text-sm text-muted-foreground">Dias prom.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar NCR, orden, titulo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-[160px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="open">Abiertas</SelectItem>
            <SelectItem value="investigation">Investigacion</SelectItem>
            <SelectItem value="corrective_action">Accion Correctiva</SelectItem>
            <SelectItem value="verification">Verificacion</SelectItem>
            <SelectItem value="closed">Cerradas</SelectItem>
          </SelectContent>
        </Select>

        <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as any)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="critical">Criticas</SelectItem>
            <SelectItem value="major">Mayores</SelectItem>
            <SelectItem value="minor">Menores</SelectItem>
          </SelectContent>
        </Select>

        {canManage && (
          <Button onClick={() => setFormDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva NCR
          </Button>
        )}
      </div>

      {/* NCR Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredNCRs && filteredNCRs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NCR</TableHead>
                  <TableHead>Titulo</TableHead>
                  <TableHead>Severidad</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Orden</TableHead>
                  <TableHead>Asignado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNCRs.map((ncr) => (
                  <TableRow
                    key={ncr.id}
                    className={cn(ncr.is_overdue && 'bg-red-50 dark:bg-red-950/20')}
                  >
                    <TableCell className="font-medium">
                      {ncr.ncr_number}
                      {ncr.is_overdue && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          Vencida
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {ncr.title}
                    </TableCell>
                    <TableCell>
                      <Badge className={getSeverityColor(ncr.severity)}>
                        {getSeverityLabel(ncr.severity)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getStatusColor(ncr.status)}>
                        {getStatusLabel(ncr.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {ncr.work_order_number || '-'}
                    </TableCell>
                    <TableCell>
                      {ncr.assignee_name || 'Sin asignar'}
                    </TableCell>
                    <TableCell>
                      {format(new Date(ncr.opened_at), 'dd/MM/yy', { locale: es })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewNCR(ncr.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay NCRs registradas</p>
              {canManage && (
                <Button
                  variant="link"
                  onClick={() => setFormDialogOpen(true)}
                  className="mt-2"
                >
                  Crear primera NCR
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <NCRFormDialog
        open={formDialogOpen}
        onOpenChange={handleCloseForm}
        ncr={editingNCR}
      />

      <NCRDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        ncrId={selectedNCRId}
        onEdit={handleEditNCR}
      />
    </div>
  );
}

export default NCRList;
